import { useCallback } from 'react';

import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';

import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { seconds } from '@utils/milliseconds';

import { useFeature } from 'features/authentication';
import { useModal } from 'features/modals';
import type { ModalType } from 'features/modals/modals-map';
import { usePermissions } from 'features/permissions';
import type { IJob, IJobStage } from 'models/types';

import { getJobTriggers, JobTrigger, TriggerType } from './api';
import { jobTriggersQueryKey } from './query-keys';

import { useAddToJourneySidebar } from '../add-to-journey-sidebar';
import { getToken } from '../api';
import { isJourneysTokenConnected, TOKEN_STALE_TIME, tokenQueryKey } from '../hooks';

/** Trigger list is re-read on every prompt; this keeps a burst of moves to one request. */
const TRIGGER_PROMPT_STALE_TIME = seconds(30);

export type JourneyOutcome = Exclude<TriggerType, 'stage'>;

/**
 * The modal type to chain the journey prompt onto when moving into this
 * stage will auto-open a modal of its own (Application.changeStage does that
 * for stages with a dynamicStage entry), or null when the stage opens
 * nothing and the prompt can fire directly. Mirrors changeStage's own
 * exception: HIRE_APPLICANT does not open for an application that is already
 * hired, so nothing to chain onto in that case either.
 */
const stageAutoOpenModalType = ({
  job,
  stageId,
  isApplicationHired,
}: {
  job: IJob;
  stageId: number;
  isApplicationHired: boolean;
}): ModalType | null => {
  const config = job.jobStages.find((stage: IJobStage) => stage.id === stageId)?.dynamicStage[0];
  if (!config) return null;
  if (config.autoOpenModal === 'HIRE_APPLICANT' && isApplicationHired) return null;
  return config.autoOpenModal;
};

/**
 * Journey triggers never fire headlessly — they are UI behavior. After
 * applicant(s) successfully move into a stage with a stage trigger (or are
 * hired/rejected with a matching outcome trigger), this hook opens the
 * add-to-journey sidebar prefilled with the trigger's journey so the
 * recruiter reviews the field values and sends explicitly.
 */
export const useStageEntryJourneyPrompt = (jobId: number) => {
  const hasIntegrationFlag = useFeature('FEATURE_INTEGRATION_JOURNEYS');
  const { hasRolePermission } = usePermissions();
  const showAddToJourneySidebar = useAddToJourneySidebar();
  const queryClient = useQueryClient();
  const { showError } = useToaster();
  const { t } = useScopedTranslate(locale);
  const { setModalAfterClose } = useModal();

  const isEnabled = hasIntegrationFlag && jobId > 0;

  const promptForTrigger = useCallback(
    async (applicationIds: number[], isMatch: (trigger: JobTrigger) => boolean) => {
      if (!isEnabled || applicationIds.length === 0) return;
      const requiredPermission =
        applicationIds.length === 1
          ? 'application:add-journeys'
          : 'application:bulk-create-journeys';
      if (!hasRolePermission(requiredPermission)) return;

      // The connection is resolved here rather than through useToken in the
      // hook body on purpose. This is a one-shot side effect: a synchronous
      // gate reading tokenQuery.data would treat "still in flight" as
      // "disconnected" and drop the prompt for good, with nothing to retry it
      // once the token lands. Awaiting instead defers rather than discards.
      //
      // ok and valid are separate flags — expired credentials come back as
      // {ok: true, valid: false} — so both are required, matching the
      // sidebar's own isConnected. Without that, a stage move would be
      // answered by a sidebar that immediately reports invalid credentials.
      try {
        const token = await queryClient.fetchQuery({
          queryKey: tokenQueryKey(),
          queryFn: getToken,
          staleTime: TOKEN_STALE_TIME,
        });
        if (!isJourneysTokenConnected(token)) return;
      } catch (error) {
        // Not thrown: callers fire this un-awaited, so a throw surfaces as an
        // unhandled rejection and nothing else. But not throwing is not the same as
        // not reporting — without this the recruiter cannot tell "the automation
        // failed" from "there is no trigger on this stage".
        Sentry.captureException(error);
        showError(t('triggerPromptFailed'));
        return;
      }

      // fetchQuery, not ensureQueryData: in react-query v4 ensureQueryData
      // returns whatever is cached regardless of staleness, so a trigger
      // created while no useJobTriggers observer was mounted would never be
      // seen. fetchQuery honours staleTime — fresh resolves without a request,
      // stale refetches — which also covers the race where this runs before a
      // background query settles.
      let triggers: JobTrigger[];
      try {
        triggers = await queryClient.fetchQuery({
          queryKey: jobTriggersQueryKey(jobId),
          queryFn: () => getJobTriggers(jobId),
          staleTime: TRIGGER_PROMPT_STALE_TIME,
        });
      } catch (error) {
        // The move already succeeded; the prompt is an enhancement on top of
        // it. Callers fire this un-awaited, so a throw here would surface as
        // an unhandled rejection and nothing else.
        Sentry.captureException(error);
        showError(t('triggerPromptFailed'));
        return;
      }

      const trigger = triggers.find((candidate) => candidate.isAvailable && isMatch(candidate));
      if (!trigger) return;
      showAddToJourneySidebar({
        applications: applicationIds,
        jobId,
        initialJourneyId: trigger.journeyId,
        initialJourneyTitle: trigger.journeyTitle,
      });
    },
    [isEnabled, hasRolePermission, queryClient, jobId, showAddToJourneySidebar, showError, t]
  );

  /** Open the prefilled sidebar when the entered stage has a stage trigger. */
  const promptStageEntryJourney = useCallback(
    (applicationIds: number[], stageId: number) =>
      promptForTrigger(
        applicationIds,
        (trigger) => trigger.triggerType === 'stage' && trigger.jobStage === stageId
      ),
    [promptForTrigger]
  );

  /** Open the prefilled sidebar when a hired/rejected outcome trigger exists. */
  const promptOutcomeJourney = useCallback(
    (applicationIds: number[], outcome: JourneyOutcome) =>
      promptForTrigger(applicationIds, (trigger) => trigger.triggerType === outcome),
    [promptForTrigger]
  );

  /**
   * Stage-move entry point. When the entered stage auto-opens a modal, the
   * prompt is chained to fire once that modal closes (any route) instead of
   * stacking a second interrupt; the hire/reject modals clear the
   * registration on success so the matching outcome prompt wins. Otherwise
   * prompts immediately.
   */
  const promptOrChainStageEntryJourney = useCallback(
    (
      applicationIds: number[],
      stageId: number,
      { job, isApplicationHired }: { job: IJob; isApplicationHired: boolean }
    ) => {
      const modalType = stageAutoOpenModalType({ job, stageId, isApplicationHired });
      if (modalType) {
        setModalAfterClose(modalType, () => promptStageEntryJourney(applicationIds, stageId));
        return;
      }
      promptStageEntryJourney(applicationIds, stageId);
    },
    [setModalAfterClose, promptStageEntryJourney]
  );

  return { promptStageEntryJourney, promptOutcomeJourney, promptOrChainStageEntryJourney };
};

const locale = {
  en: { triggerPromptFailed: 'Could not check journey automations for this stage' },
  is: {
    triggerPromptFailed:
      'Ekki tókst að athuga sjálfvirkar ræsingar Journey-ferla fyrir þennan dálk',
  },
};
